import { Trans, useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { foyerApi, type AppRecord } from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "../components/toast";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { usePermission } from "../auth/access";
import { ItemFormSurface, type Dismiss } from "../components/EditDrawer";
import AssetField from "../components/AssetField";
import ColorSwatch from "../components/ColorSwatch";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import {
  APP_CERTIFICATIONS,
  CERTIFICATION_LABEL,
  isAppCertification,
} from "@shared/app-certification";
import {
  LAUNCH_PLATFORMS,
  LAUNCH_PLATFORM_LABELS,
  appCoverage,
  resolveLaunchTarget,
  type LaunchPlatform,
} from "@shared/app-resolve";

/**
 * Admin-only global catalogue of Apps. Non-admin users can still GET the
 * list (read-only), but only admins reach this page (nav item is hidden)
 * and the mutation endpoints reject anyone else with 403.
 *
 * Premises consume this catalogue: per-premise selection + overrides are
 * resolved against it in `shared/app-merge.ts`.
 */
/** Short platform tags, for the coverage chips. */
const PLATFORM_TAGS: Record<Exclude<LaunchPlatform, "web">, string> = {
  android: "Android",
  tizen: "Tizen",
  webos: "webOS",
  ios: "iOS",
};

const NAME_PLACEHOLDER = "Netflix";
const WORDMARK_PLACEHOLDER = "NETFLIX";
const LETTER_PLACEHOLDER = "N";
const ANDROID_SLUG_PLACEHOLDER = "com.netflix.ninja";
const WEBOS_ID_PLACEHOLDER = "netflix";
const IOS_SCHEME_PLACEHOLDER = "nflx://";
const WEB_URL_PLACEHOLDER = "https://www.netflix.com/";

/**
 * Which platforms this catalogue entry can open on - the "streaming-app
 * aggregation" view of one row. A filled chip means a native launch id for that
 * OS; an outlined "Web" chip means only the https fallback, which opens a
 * browser rather than the app. A platform with neither is shown greyed so the
 * gap is visible at a glance instead of only surfacing as a dead tile on a TV.
 * The name is part of the input on purpose: in a browser a well-known app opens
 * its own site even with nothing filled in, and the chips must say so.
 */
function CoverageChips({
  app,
}: {
  app: {
    name: string;
    androidSlug: string | null;
    tizenId: string | null;
    webosId: string | null;
    iosScheme: string | null;
    webUrl: string | null;
  };
}) {
  const { t } = useTranslation("premises");
  const covered = new Set(appCoverage(app));
  return (
    // One row, always: the five chips are the at-a-glance coverage view, and
    // wrapping split it across two lines on every row.
    <Stack direction="row" spacing={0.5} flexWrap="nowrap" useFlexGap>
      {LAUNCH_PLATFORMS.map((p) => {
        const native = covered.has(p) && p !== "web";
        const target = resolveLaunchTarget(app, p);
        const fallback = !native && target.kind === "web";
        const platform =
          p === "web"
            ? t("appCatalogPage.platformWeb")
            : LAUNCH_PLATFORM_LABELS[p];
        const title = native
          ? t("appCatalogPage.coverage.native", { platform })
          : fallback
            ? target.source === "knownApp"
              ? t("appCatalogPage.coverage.knownSite", {
                  platform,
                  name: app.name,
                  id: target.id,
                })
              : t("appCatalogPage.coverage.webAddress", { platform })
            : t("appCatalogPage.coverage.none", { platform });
        return (
          <Tooltip key={p} title={title}>
            <Chip
              size="small"
              label={
                p === "web" ? t("appCatalogPage.platformWeb") : PLATFORM_TAGS[p]
              }
              color={
                native || (p === "web" && covered.has(p))
                  ? "primary"
                  : "default"
              }
              variant={native || covered.has(p) ? "filled" : "outlined"}
              sx={
                native || fallback || covered.has(p)
                  ? undefined
                  : { opacity: 0.4 }
              }
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export default function AppCatalogPage() {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  // Mutation affordances follow the same permission the API enforces.
  const isAdmin = usePermission("tv-layout", "administrate", "nenda");
  const [items, setItems] = useState<AppRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: AppRecord } | null
  >(null);

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listApps();
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(t, e));
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onDelete(row: AppRecord) {
    if (!window.confirm(t("appCatalogPage.deleteConfirm", { name: row.name })))
      return;
    try {
      await foyerApi.deleteApp(row.id);
      toast.success(t("appCatalogPage.deleted", { name: row.name }));
      await refresh();
    } catch (e) {
      toast.error(describeError(t, "deleteApp", e, { name: row.name }));
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("appCatalogPage.eyebrow")}
        title={t("appCatalogPage.title")}
        actions={
          isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: "create" })}
              data-testid="app-add"
            >
              {t("appCatalogPage.newApp")}
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="apps-list">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>{t("common:labels.name")}</TableCell>
                <TableCell>{t("appCatalogPage.fields.wordmark")}</TableCell>
                {/* 1% + nowrap = exactly as wide as the chip row, never wrapped. */}
                <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                  {t("appCatalogPage.columns.platforms")}
                </TableCell>
                {isAdmin && <TableCell sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((row) => (
                <TableRow key={row.id} hover data-testid={`app-row-${row.id}`}>
                  <TableCell>
                    <Avatar
                      src={row.logoUrl ?? undefined}
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: row.color,
                        color: row.text,
                        fontSize: 14,
                      }}
                    >
                      {row.letter || row.name.charAt(0)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightMedium" }}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.wordmark}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <CoverageChips app={row} />
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={t("common:buttons.edit")}>
                        <IconButton
                          size="small"
                          aria-label={t("appCatalogPage.editNamed", {
                            name: row.name,
                          })}
                          onClick={() =>
                            setDialog({ mode: "edit", record: row })
                          }
                          data-testid={`app-edit-${row.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:buttons.delete")}>
                        <IconButton
                          size="small"
                          aria-label={t("appCatalogPage.deleteNamed", {
                            name: row.name,
                          })}
                          onClick={() => onDelete(row)}
                          data-testid={`app-delete-${row.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4}>
                    <Typography variant="caption" color="text.secondary">
                      <Trans
                        t={t}
                        i18nKey="appCatalogPage.empty"
                        components={{ strong: <strong /> }}
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dialog && (
        <AppDialog
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

/** Full-width section heading inside the two-column app form grid. */
function SectionLabel({
  children,
  first = false,
}: {
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: first ? 0 : 1 }}>
      {!first && <Divider sx={{ mb: 1.5 }} />}
      <Typography
        variant="overline"
        sx={{
          fontWeight: "fontWeightSemibold",
          color: "text.secondary",
          lineHeight: 1.6,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function AppDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: AppRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const [name, setName] = useState(initial?.name ?? "");
  const [color, setColor] = useState(initial?.color ?? "#222222");
  const [text, setText] = useState(initial?.text ?? "#ffffff");
  const [wordmark, setWordmark] = useState(initial?.wordmark ?? "");
  const [letter, setLetter] = useState(initial?.letter ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [androidSlug, setAndroidSlug] = useState(initial?.androidSlug ?? "");
  const [tizenId, setTizenId] = useState(initial?.tizenId ?? "");
  const [webosId, setWebosId] = useState(initial?.webosId ?? "");
  const [iosScheme, setIosScheme] = useState(initial?.iosScheme ?? "");
  const [webUrl, setWebUrl] = useState(initial?.webUrl ?? "");
  const [certification, setCertification] = useState(
    initial?.certification ?? ""
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Only an entry that is BOTH enrolled and already carrying its official asset
  // is locked: a freshly-enrolled one still needs its artwork set once, and
  // stranding it with no logo would be a worse failure than the one this
  // prevents. Mirrors `appCatalogStore.update`.
  const artworkLocked =
    isAppCertification(initial?.certification) && Boolean(initial?.logoUrl);

  async function save(dismiss: Dismiss) {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name,
        color,
        text,
        wordmark,
        letter,
        logoUrl: logoUrl.trim() || null,
        androidSlug: androidSlug.trim() || null,
        tizenId: tizenId.trim() || null,
        webosId: webosId.trim() || null,
        iosScheme: iosScheme.trim() || null,
        webUrl: webUrl.trim() || null,
        certification: certification || null,
      };
      if (initial) {
        await foyerApi.updateApp(initial.id, payload);
        toast.success(t("appCatalogPage.updated", { name }));
      } else {
        await foyerApi.createApp(payload);
        toast.success(t("appCatalogPage.added", { name }));
      }
      // Slide the form away first; the page then refreshes and unmounts it.
      dismiss(() => void onSaved());
    } catch (e) {
      setErr(friendlyError(t, e));
      toast.error(
        name.trim()
          ? describeError(t, "saveContact", e, { name: name.trim() })
          : describeError(t, "saveApp", e)
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      onClose={onClose}
      title={initial ? t("appCatalogPage.editApp") : t("appCatalogPage.newApp")}
      maxWidth="md"
      drawerWidth={720}
      busy={busy}
      data-testid="app-form"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={busy || !name.trim()}
            data-testid="app-save"
          >
            {busy ? t("common:states.saving") : t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      {/*
       * Two-column grid: the catalogue has a dozen fields (five per-platform
       * launch identifiers alone), so a single vertical stack forced a lot of
       * scrolling. Sections span the full width and act as visual anchors.
       */}
      <Box
        sx={{
          pt: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 2,
          rowGap: 2,
          alignItems: "start",
        }}
      >
        <SectionLabel first>
          {t("appCatalogPage.sections.identity")}
        </SectionLabel>
        <TextField
          label={t("common:labels.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          autoFocus
          placeholder={NAME_PLACEHOLDER}
          inputProps={{ "data-testid": "app-name" }}
          // Full row, so Wordmark + Letter pair up beneath it.
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />
        <TextField
          label={t("appCatalogPage.fields.wordmark")}
          value={wordmark}
          onChange={(e) => setWordmark(e.target.value)}
          size="small"
          placeholder={WORDMARK_PLACEHOLDER}
        />
        <TextField
          label={t("appCatalogPage.fields.letter")}
          value={letter}
          onChange={(e) => setLetter(e.target.value)}
          size="small"
          placeholder={LETTER_PLACEHOLDER}
        />

        <SectionLabel>{t("common:labels.appearance")}</SectionLabel>
        <Box>
          {artworkLocked ? (
            // NFLX-5. Shown, never offered for replacement: the brand terms
            // name a specific official asset, and this catalogue is global,
            // so one re-upload would decertify every premise showing the app
            // at once. The server refuses the write too - this is the
            // explanation, not the enforcement.
            <Stack spacing={1}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "fontWeightMedium" }}
              >
                {t("appCatalogPage.fields.logo")}
              </Typography>
              {logoUrl && (
                <Box
                  component="img"
                  src={logoUrl}
                  alt=""
                  data-testid="app-logo-locked-preview"
                  sx={{
                    maxWidth: 220,
                    maxHeight: 72,
                    objectFit: "contain",
                    bgcolor: "#fff",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="app-logo-locked"
              >
                {t("appCatalogPage.artworkLocked", {
                  programme: CERTIFICATION_LABEL[certification as "netflix"],
                })}
              </Typography>
            </Stack>
          ) : (
            <AssetField
              label={t("appCatalogPage.fields.logo")}
              value={logoUrl}
              onChange={setLogoUrl}
              preview
              testId="app-logo"
            />
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: "fontWeightMedium" }}>
              {t("appCatalogPage.fields.background")}
            </Typography>
            <ColorSwatch
              value={color}
              onChange={setColor}
              testId="app-color"
              ariaLabel={t("appCatalogPage.fields.backgroundColour")}
            />
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: "fontWeightMedium" }}>
              {t("appCatalogPage.fields.text")}
            </Typography>
            <ColorSwatch
              value={text}
              onChange={setText}
              testId="app-text"
              ariaLabel={t("appCatalogPage.fields.textColour")}
            />
          </Stack>
        </Stack>

        <SectionLabel>{t("appCatalogPage.sections.launchIds")}</SectionLabel>
        <TextField
          label={t("appCatalogPage.fields.androidSlug")}
          value={androidSlug}
          onChange={(e) => setAndroidSlug(e.target.value)}
          size="small"
          placeholder={ANDROID_SLUG_PLACEHOLDER}
          inputProps={{ "data-testid": "app-android-slug" }}
        />
        <TextField
          label={t("appCatalogPage.fields.tizenId")}
          value={tizenId}
          onChange={(e) => setTizenId(e.target.value)}
          size="small"
          placeholder="3201907018807"
          inputProps={{ "data-testid": "app-tizen-id" }}
        />
        <TextField
          label={t("appCatalogPage.fields.webosId")}
          value={webosId}
          onChange={(e) => setWebosId(e.target.value)}
          size="small"
          placeholder={WEBOS_ID_PLACEHOLDER}
          inputProps={{ "data-testid": "app-webos-id" }}
        />
        <TextField
          label={t("appCatalogPage.fields.iosScheme")}
          value={iosScheme}
          onChange={(e) => setIosScheme(e.target.value)}
          size="small"
          placeholder={IOS_SCHEME_PLACEHOLDER}
          inputProps={{ "data-testid": "app-ios-scheme" }}
        />
        <TextField
          label={t("appCatalogPage.fields.webUrl")}
          value={webUrl}
          onChange={(e) => setWebUrl(e.target.value)}
          size="small"
          placeholder={WEB_URL_PLACEHOLDER}
          helperText={t("appCatalogPage.fields.webUrlHelp")}
          inputProps={{ "data-testid": "app-web-url" }}
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />

        <SectionLabel>
          {t("appCatalogPage.sections.certification")}
        </SectionLabel>
        <TextField
          select
          label={t("appCatalogPage.fields.certification")}
          value={certification}
          onChange={(e) => setCertification(e.target.value)}
          size="small"
          helperText={t("appCatalogPage.fields.certificationHelp")}
          inputProps={{ "data-testid": "app-certification" }}
          sx={{ gridColumn: { sm: "1 / -1" } }}
        >
          <MenuItem value="">
            {t("appCatalogPage.fields.noCertification")}
          </MenuItem>
          {APP_CERTIFICATIONS.map((c) => (
            <MenuItem key={c} value={c}>
              {CERTIFICATION_LABEL[c]}
            </MenuItem>
          ))}
        </TextField>
        {err && (
          <Typography
            variant="caption"
            color="error"
            sx={{ gridColumn: { sm: "1 / -1" } }}
          >
            {err}
          </Typography>
        )}
      </Box>
    </ItemFormSurface>
  );
}
